import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import Datepicker from "../../../../components/Datepicker";
import TextButton from "../../../../components/buttons/TextButton";

const formatDateValue = (value) => (value ? value.format("YYYY-MM-DD") : null);

const AuditLogsExportModal = ({
  open,
  onClose,
  onExport,
  users = [],
  isExporting = false,
}) => {
  const colors = useColors();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(dayjs());
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setFromDate(null);
      setToDate(dayjs());
      setSelectedEmails([]);
      setError("");
    }
  }, [open]);

  const validateDates = () => {
    if (fromDate && toDate && fromDate.isAfter(toDate, "day")) {
      setError("From Date cannot be later than To Date.");
      return false;
    }
    setError("");
    return true;
  };

  const handleExport = () => {
    if (!validateDates()) return;

    onExport({
      userEmails: selectedEmails,
      fromDate: formatDateValue(fromDate),
      toDate: formatDateValue(toDate),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "10px",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Export Audit Log</Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" sx={{ color: colors.text_secondary }}>
          Select a date range and users to export the matching audit log
          entries.
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Box flex={1} minWidth="220px">
            <Typography variant="caption" sx={{ color: colors.text_secondary }}>
              From Date
            </Typography>
            <Datepicker
              value={fromDate}
              onChange={(value) => {
                setFromDate(value);
                setError("");
              }}
              disableFuture
              format="YYYY-MM-DD"
            />
          </Box>
          <Box flex={1} minWidth="220px">
            <Typography variant="caption" sx={{ color: colors.text_secondary }}>
              To Date
            </Typography>
            <Datepicker
              value={toDate}
              onChange={(value) => {
                setToDate(value);
                setError("");
              }}
              disableFuture
              format="YYYY-MM-DD"
            />
          </Box>
        </Box>
        {error && (
          <FormHelperText error sx={{ marginTop: "-4px" }}>
            {error}
          </FormHelperText>
        )}

        <Box>
          <Typography variant="caption" sx={{ color: colors.text_secondary }}>
            User Email
          </Typography>
          <Autocomplete
            multiple
            size="small"
            options={users}
            value={selectedEmails}
            onChange={(_, value) => setSelectedEmails(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select users (optional)"
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <TextButton
          color="inherit"
          onClick={onClose}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </TextButton>
        <TextButton
          onClick={handleExport}
          color="primary"
          disabled={isExporting}
        >
          {isExporting ? "Exporting..." : "Export"}
        </TextButton>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogsExportModal;
